// src/hooks/useViewport.js
// Shared viewport/pointer capabilities via matchMedia. One set of module-level
// listeners serves every consumer; the snapshot object is only replaced when a
// query result changes so it is safe to use as an effect/memo dependency.
//
// Breakpoints mirror Tailwind md (768) / lg (1024) and the existing inline
// `window.innerWidth < 768` checks in the calendar. Note the index.css phone
// typography tier is `max-width: 768px` inclusive, so a 768px-wide iPad
// portrait is CSS-phone but JS-tablet — identical to today's behavior.

import { useSyncExternalStore } from 'react';

const QUERIES = {
  isPhone: '(max-width: 767.98px)',
  isTablet: '(min-width: 768px) and (max-width: 1023.98px)',
  isDesktop: '(min-width: 1024px)',
  isWide: '(min-width: 1280px)', // Tailwind xl
  isCoarsePointer: '(pointer: coarse)',
  canHover: '(hover: hover)',
};

const SERVER_SNAPSHOT = Object.freeze({
  isPhone: false,
  isTablet: false,
  isDesktop: true,
  isWide: true,
  isCoarsePointer: false,
  canHover: true,
});

let mqls = null;
let snapshot = SERVER_SNAPSHOT;
const listeners = new Set();

function compute() {
  const next = {};
  for (const key in mqls) next[key] = mqls[key].matches;
  snapshot = next;
}

function handleChange() {
  compute();
  listeners.forEach((listener) => listener());
}

function init() {
  if (mqls || typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
  mqls = {};
  for (const key in QUERIES) mqls[key] = window.matchMedia(QUERIES[key]);
  compute();
  for (const key in mqls) {
    const mql = mqls[key];
    if (mql.addEventListener) mql.addEventListener('change', handleChange);
    else mql.addListener(handleChange); // older iOS Safari
  }
}

function subscribe(listener) {
  init();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  init();
  return snapshot;
}

/**
 * @returns {{ isPhone: boolean, isTablet: boolean, isDesktop: boolean, isWide: boolean, isCoarsePointer: boolean, canHover: boolean }}
 */
export function useViewport() {
  return useSyncExternalStore(subscribe, getSnapshot, () => SERVER_SNAPSHOT);
}
